import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  LayoutDashboard, BookOpen, Settings, Plus, Trash2, Edit2,
  Clock, Video, Save, Loader2, Package, X, GraduationCap, Gift
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
        <Link to="/admin/perks" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Gift size={20} />
          <span>Perks & Tiers</span>
        </Link>
        <Link to="/admin/training" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <GraduationCap size={20} />
          <span>Staff Training</span>
        </Link>
      </nav>
    </aside>
  );
};

export const AdminClassSettings = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [hourPackages, setHourPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassroom, setSelectedClassroom] = useState('all');
  const [editingClass, setEditingClass] = useState(null);
  const [newPackageHours, setNewPackageHours] = useState('');
  const [isAddingPackage, setIsAddingPackage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classroomsRes, classesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/classrooms`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/hour-packages`)
      ]);
      setClassrooms(classroomsRes.data);
      setClasses(classesRes.data);
      setHourPackages(packagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClass = async () => {
    if (!editingClass) return;
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/classes/${editingClass.id}`, {
        duration_minutes: editingClass.duration_minutes,
        class_type: editingClass.class_type,
        frequency: editingClass.frequency,
        delivery_mode: editingClass.delivery_mode,
      });
      toast.success('Class updated successfully');
      fetchData();
      setEditingClass(null);
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPackage = async () => {
    if (!newPackageHours || isNaN(parseInt(newPackageHours))) {
      toast.error('Please enter a valid hour count');
      return;
    }
    
    setIsAddingPackage(true);
    try {
      await axios.post(`${API}/hour-packages`, { hours: parseInt(newPackageHours) });
      toast.success('Hour package added successfully');
      setNewPackageHours('');
      fetchData();
    } catch (error) {
      console.error('Error adding package:', error);
      toast.error(error.response?.data?.detail || 'Failed to add package');
    } finally {
      setIsAddingPackage(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await axios.delete(`${API}/hour-packages/${packageId}`);
      toast.success('Package deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const filteredClasses = selectedClassroom === 'all' 
    ? classes 
    : classes.filter(c => c.classroom_id === selectedClassroom);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />
      
      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Class Settings</h1>
            <p className="text-[#A1A1AA]">
              Configure class durations, types, frequency, and delivery modes
            </p>
          </div>

          <Tabs defaultValue="classes" className="space-y-6">
            <TabsList className="bg-[#121212] border border-[#2A2A2A]">
              <TabsTrigger value="classes" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                Class Configuration
              </TabsTrigger>
              <TabsTrigger value="packages" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                Hour Packages
              </TabsTrigger>
            </TabsList>

            {/* Classes Tab */}
            <TabsContent value="classes" className="space-y-6">
              {/* Filter */}
              <div className="flex items-center gap-4">
                <Label className="text-[#A1A1AA]">Filter by Classroom:</Label>
                <Select value={selectedClassroom} onValueChange={setSelectedClassroom}>
                  <SelectTrigger className="w-[250px] bg-[#121212] border-[#2A2A2A] text-[#EDEDED]" data-testid="filter-classroom">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    <SelectItem value="all" className="text-[#EDEDED]">All Classrooms</SelectItem>
                    {classrooms.map(cr => (
                      <SelectItem key={cr.id} value={cr.id} className="text-[#EDEDED]">
                        {cr.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Classes Table */}
              <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2A2A2A] hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA]">Class Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Classroom</TableHead>
                      <TableHead className="text-[#A1A1AA]">Duration</TableHead>
                      <TableHead className="text-[#A1A1AA]">Type</TableHead>
                      <TableHead className="text-[#A1A1AA]">Frequency</TableHead>
                      <TableHead className="text-[#A1A1AA]">Delivery</TableHead>
                      <TableHead className="text-[#A1A1AA]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClasses.map(cls => (
                      <TableRow key={cls.id} className="border-[#2A2A2A] hover:bg-white/5">
                        <TableCell className="text-[#EDEDED] font-medium max-w-[200px] truncate">
                          {cls.name}
                        </TableCell>
                        <TableCell className="text-[#A1A1AA]">{cls.classroom_name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-[#EDEDED]">
                            <Clock size={14} className="text-[#D4AF37]" />
                            {cls.duration_minutes || 60} min
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={cls.class_type === 'compulsory' 
                              ? 'border-red-500/50 text-red-400' 
                              : 'border-[#D4AF37]/50 text-[#D4AF37]'
                            }
                          >
                            {cls.class_type || 'elective'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[#A1A1AA]">{cls.frequency || 'weekly'}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-[#EDEDED]">
                            <Video size={14} className="text-[#A1A1AA]" />
                            {cls.delivery_mode || 'both'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingClass(cls)}
                            className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`edit-class-${cls.id}`}
                          >
                            <Edit2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Packages Tab */}
            <TabsContent value="packages" className="space-y-6">
              {/* Add New Package */}
              <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
                <h3 className="font-heading text-lg text-[#F5F5DC] mb-4">Add New Hour Package</h3>
                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-[200px]">
                    <Label className="text-[#A1A1AA]">Hours per Semester</Label>
                    <Input
                      type="number"
                      value={newPackageHours}
                      onChange={(e) => setNewPackageHours(e.target.value)}
                      placeholder="e.g., 48"
                      className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                      data-testid="input-new-package"
                    />
                  </div>
                  <Button
                    onClick={handleAddPackage}
                    disabled={isAddingPackage}
                    className="btn-gold"
                    data-testid="btn-add-package"
                  >
                    {isAddingPackage ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <Plus size={16} className="mr-2" />
                    )}
                    Add Package
                  </Button>
                </div>
              </div>

              {/* Existing Packages */}
              <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
                <h3 className="font-heading text-lg text-[#F5F5DC] mb-4">Existing Hour Packages</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {hourPackages.map(pkg => (
                    <div 
                      key={pkg.id}
                      className="flex items-center justify-between p-4 bg-black/30 border border-[#2A2A2A] rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Package size={18} className="text-[#D4AF37]" />
                        <span className="text-[#EDEDED] font-medium">{pkg.hours}h</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePackage(pkg.id)}
                        className="text-red-400 hover:text-red-400 hover:bg-red-500/10 p-1 h-auto"
                        data-testid={`delete-package-${pkg.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Edit Class Dialog */}
      <Dialog open={!!editingClass} onOpenChange={() => setEditingClass(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-[#EDEDED]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#F5F5DC]">
              Edit Class Configuration
            </DialogTitle>
          </DialogHeader>
          
          {editingClass && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-black/30 rounded-lg">
                <p className="text-sm text-[#A1A1AA]">Class Name</p>
                <p className="text-[#F5F5DC] font-medium">{editingClass.name}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Duration (minutes)</Label>
                <Input
                  type="number"
                  value={editingClass.duration_minutes || 60}
                  onChange={(e) => setEditingClass({...editingClass, duration_minutes: parseInt(e.target.value)})}
                  className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                  data-testid="edit-duration"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Class Type</Label>
                <Select 
                  value={editingClass.class_type || 'elective'}
                  onValueChange={(value) => setEditingClass({...editingClass, class_type: value})}
                >
                  <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    <SelectItem value="elective" className="text-[#EDEDED]">Elective</SelectItem>
                    <SelectItem value="compulsory" className="text-[#EDEDED]">Compulsory</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Frequency</Label>
                <Select 
                  value={editingClass.frequency || 'weekly'}
                  onValueChange={(value) => setEditingClass({...editingClass, frequency: value})}
                >
                  <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="edit-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    <SelectItem value="weekly" className="text-[#EDEDED]">Weekly</SelectItem>
                    <SelectItem value="bi-weekly" className="text-[#EDEDED]">Bi-weekly</SelectItem>
                    <SelectItem value="monthly" className="text-[#EDEDED]">Monthly</SelectItem>
                    <SelectItem value="on-demand" className="text-[#EDEDED]">On Demand</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Delivery Mode</Label>
                <Select 
                  value={editingClass.delivery_mode || 'both'}
                  onValueChange={(value) => setEditingClass({...editingClass, delivery_mode: value})}
                >
                  <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="edit-delivery">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    <SelectItem value="live" className="text-[#EDEDED]">Live Only</SelectItem>
                    <SelectItem value="recording" className="text-[#EDEDED]">Recording Only</SelectItem>
                    <SelectItem value="both" className="text-[#EDEDED]">Both (Live + Recording)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditingClass(null)}
              className="border-[#2A2A2A] text-[#A1A1AA]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateClass}
              disabled={isSaving}
              className="btn-gold"
              data-testid="btn-save-class"
            >
              {isSaving ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClassSettings;
